import Stack from '@mui/material/Stack/Stack';
import { useTranslation } from 'react-i18next';
import { FormikProvider, useFormik } from 'formik';
import { useState, useCallback } from 'react';
import { useAdyenAPM } from './hooks/use-adyen-apm';
import { InputField } from '../../../../../InputField/InputField';
import { AddressElement } from '../../../AddressElement/AddressElement';
import { PayButton } from '../../../PaymentForm/components/PayButton/PayButton';
import { useAddressElementRef } from '../../../AddressElement/hooks/use-address-element-ref';
import { useShowTaxCalculationFields } from '../../../../hooks/use-show-tax-calculation-fields';
import { PaymentElementContainer } from '../../../PaymentElementContainer/PaymentElementContainer';
import { useDynamicFormAdyen } from './hooks/use-dynamic-form-adyen';
import { ADYEN_APM_FIELDS, AdyenAPMAdditionalInputs } from '../../types';
import PhoneNumberInput from '../../../../../PhoneNumberInput/PhoneNumberInput';
import { useCheckout } from '../../../../../../hooks/use-checkout';
import { AdyenApmScreenLock } from './AdyenApmScreenLock/AdyenApmScreenLock';
import { CountryCode, isValidPhoneNumber } from 'libphonenumber-js';
import { validateBillingAddressUtil } from './hooks/utils';
import { useAppConfig } from '../../../../../../hooks/use-app-config';

export function AydenAPM() {
  const { handlePay, isAdyenApmBroadcastLock } = useAdyenAPM();
  const { AddressElementRef } = useAddressElementRef();
  const { formik, formFields } = useDynamicFormAdyen();
  const { useTaxCalculationField } = useShowTaxCalculationFields();
  const { checkoutData } = useCheckout();
  const { featureFlags } = useAppConfig();
  const shouldHideFieldsLabels = featureFlags?.checkout_remove_field_labels;
  const [billingAddressError, setBillingAddressError] = useState<string>();

  const { t } = useTranslation(undefined, { keyPrefix: 'checkout' });

  const hasAddressField: boolean = formFields.some(
    (field) => field.apm_key === ADYEN_APM_FIELDS.BILLING_ADDRESS,
  );
  const shouldShowTaxCalculationField = useTaxCalculationField && !hasAddressField;

  const countryCode2 =
    checkoutData?.customer.taxCountry || (checkoutData?.customer.geoLocator.countryCode2 as string);

  const validateBillingAddress = useCallback(() => {
    return validateBillingAddressUtil({
      hasAddressField,
      shouldShowTaxCalculationField,
      AddressElementRef,
      countryCode2: countryCode2,
    });
  }, [hasAddressField, shouldShowTaxCalculationField, countryCode2]);

  const handleAddressBlur = useCallback(() => {
    const { error } = validateBillingAddress();
    setBillingAddressError(error);
  }, [validateBillingAddress]);

  const onPayClick = async () => {
    await formik.submitForm();

    const isAddressValid = hasAddressField ? AddressElementRef.current?.isValid() : true;

    const { isValid: isBillingAddressValid, error } = validateBillingAddress();
    if (!isBillingAddressValid) {
      setBillingAddressError(error);
    } else {
      setBillingAddressError(undefined);
    }

    const hasPhoneField = formFields.some(
      (field) => field.apm_key === ADYEN_APM_FIELDS.TELEPHONE_NUMBER,
    );

    const isPhoneValid = hasPhoneField
      ? isValidPhoneNumber(
          (formik.values as any)[ADYEN_APM_FIELDS.TELEPHONE_NUMBER],
          countryCode2 as CountryCode,
        )
      : true;

    const allValid = formik.isValid && isAddressValid && isBillingAddressValid && isPhoneValid;
    if (allValid) {
      handlePay({
        ...formik.values,
        billingAddress: AddressElementRef.current?.values,
      } as AdyenAPMAdditionalInputs);
    }
  };

  return (
    <>
      <PaymentElementContainer
        content={
          <Stack spacing={1.5}>
            <FormikProvider value={formik}>
              {formFields.map((field) => {
                if (field.apm_key === ADYEN_APM_FIELDS.BILLING_ADDRESS) {
                  return (
                    <div key={field.apm_key}>
                      <AddressElement
                        ref={AddressElementRef}
                        zipOnly={false}
                        billingAddressError={billingAddressError}
                        onBlur={handleAddressBlur}
                      />
                    </div>
                  );
                }
                if (field.apm_key === ADYEN_APM_FIELDS.TELEPHONE_NUMBER) {
                  return (
                    <PhoneNumberInput
                      key={field.apm_key}
                      value={field.defaultValue}
                      onChange={(value) => formik.setFieldValue(field.apm_key, value)}
                      country={countryCode2}
                      name={field.apm_key}
                    />
                  );
                }
                return (
                  <InputField
                    key={field.apm_key}
                    name={field.apm_key}
                    label={shouldHideFieldsLabels ? '' : t(field.apm_key)}
                    inputProps={{
                      placeholder: t(field.apm_key),
                    }}
                  />
                );
              })}
            </FormikProvider>
            {shouldShowTaxCalculationField && (
              <div>
                <AddressElement
                  ref={AddressElementRef}
                  zipOnly={true}
                  billingAddressError={billingAddressError}
                  onBlur={handleAddressBlur}
                />
              </div>
            )}
          </Stack>
        }
      >
        <PayButton onClick={onPayClick} />
      </PaymentElementContainer>
      {isAdyenApmBroadcastLock && <AdyenApmScreenLock text={t('pleaseWaitCheckingOrderStatus')} />}
    </>
  );
}
